import type {
  UploadSpec, UploadTemplate, UploadView,
  Answer, BeforeAfter, CleanRow, Board, Impact, Intent, IntentSummary, Metrics, ScopeCandidate, Overview, Run, Scope, ScopeOptions,
  SessionInfo, User,
  Alert, AlertDetail, AlertMetric, AlertRule, AlertStatus, RuleDraft, RuleSpec,
} from "./types";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

type Listener = () => void;
const unauthorisedListeners = new Set<Listener>();

/** Called whenever the server says the session is gone, so the app can show sign-in. */
export function onUnauthorised(fn: Listener): () => void {
  unauthorisedListeners.add(fn);
  return () => unauthorisedListeners.delete(fn);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const resp = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body === undefined || body instanceof FormData ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
  });
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const data = await resp.json();
      detail = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
    } catch {
      /* not JSON */
    }
    if (resp.status === 401 && path !== "/api/session") unauthorisedListeners.forEach((fn) => fn());
    throw new ApiError(resp.status, detail);
  }
  return resp.json() as Promise<T>;
}

const b = (boardId: string) => `/api/boards/${encodeURIComponent(boardId)}`;
const i = (boardId: string, intentId: string) => `${b(boardId)}/intents/${encodeURIComponent(intentId)}`;

export const api = {
  session: () => request<SessionInfo>("GET", "/api/session"),
  login: (username: string, password: string) => request<{ user: User }>("POST", "/api/session", { username, password }),
  logout: () => request("DELETE", "/api/session"),

  boards: () => request<{ boards: Pick<Board, "id" | "name" | "updated_at">[] }>("GET", "/api/boards"),
  createBoard: (name: string) => request<Board>("POST", "/api/boards", { name }),
  board: (boardId: string) => request<{ board: Board; intents: IntentSummary[] }>("GET", b(boardId)),
  saveBoard: (board: Board) => request<{ board: Board }>("PUT", b(board.id), board),

  /** No type of work: the server infers it (and narrows the scope) when the intent runs. */
  createIntent: (boardId: string, text: string, scope: Scope, functionHint = "", parentId = "",
                 pos: { dx: number; dy: number } | null = null) =>
    request<IntentSummary>("POST", `${b(boardId)}/intents`,
      { text, scope, function_hint: functionHint, ...(parentId ? { parent_id: parentId } : {}), ...(pos ? { pos } : {}) }),
  cleanIntents: (texts: string[]) => request<{ rows: CleanRow[] }>("POST", "/api/intents/clean", { texts }),
  createBatch: (boardId: string, texts: string[], scope: Scope, functionHint = "", run = true, label = "",
                positions: { dx: number; dy: number }[] = []) =>
    request<{ batch_id: string; intents: IntentSummary[]; started: number }>("POST", `${b(boardId)}/intents/batch`,
      { texts, scope, function_hint: functionHint, run, label, positions }),
  answer: (boardId: string, intentId: string, runId = "") =>
    request<Answer>("GET", `${i(boardId, intentId)}/answer${runId ? `?run_id=${encodeURIComponent(runId)}` : ""}`),
  intent: (boardId: string, intentId: string) =>
    request<{ intent: Intent; active_run: string | null }>("GET", i(boardId, intentId)),
  updateIntent: (boardId: string, intentId: string, changes: Partial<{ text: string; scope: Scope; story_id: string; pos: { dx: number; dy: number } }>) =>
    request<IntentSummary>("PATCH", i(boardId, intentId), changes),
  deleteIntent: (boardId: string, intentId: string) => request("DELETE", i(boardId, intentId)),
  runIntent: (boardId: string, intentId: string) => request<{ run: Run }>("POST", `${i(boardId, intentId)}/run`),
  cancelIntent: (boardId: string, intentId: string) => request("POST", `${i(boardId, intentId)}/cancel`),
  /** Answer "which products did you mean?" and start the run with that scope. */
  confirmScope: (boardId: string, intentId: string, scope: Scope) =>
    request<{ intent: IntentSummary; run: Run | null }>("POST", `${i(boardId, intentId)}/scope`, { scope, run: true }),
  beforeAfter: (boardId: string, intentId: string) => request<BeforeAfter>("GET", `${i(boardId, intentId)}/before-after`),
  scopeSearch: (q: string) => request<{ matches: ScopeCandidate[] }>("GET", `/api/scope/search?q=${encodeURIComponent(q)}`),
  pushStaged: (stageIds: string[]) => request<{ entries: unknown[] }>("POST", "/api/staging/push", { stage_ids: stageIds }),
  discardStaged: (stageId: string) => request<{ entry: unknown }>("DELETE", `/api/staging/${encodeURIComponent(stageId)}`),
  impact: (boardId: string, intentId: string) => request<Impact>("GET", `${i(boardId, intentId)}/impact`),

  overview: (family: string) =>
    request<Overview>("GET", `/api/dashboard/overview${family ? `?family=${encodeURIComponent(family)}` : ""}`),
  metrics: (f: { family: string; group1: string; group2: string }) =>
    request<Metrics>("GET", `/api/metrics?${new URLSearchParams(f).toString()}`),
  scopeOptions: () => request<ScopeOptions>("GET", "/api/scope/options"),
  refreshData: () => request("POST", "/api/data/refresh"),

  /** Mass upload: the server parses the file, proposes a mapping, and keeps only the parsed cells until staged. */
  openUpload: (boardId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<UploadView>("POST", `${b(boardId)}/uploads`, form);
  },
  updateUpload: (boardId: string, uploadId: string, spec: UploadSpec) =>
    request<UploadView>("PUT", `${b(boardId)}/uploads/${encodeURIComponent(uploadId)}`, { spec }),
  stageUpload: (boardId: string, uploadId: string, saveTemplate: boolean, templateName: string) =>
    request<{ intent: IntentSummary }>("POST", `${b(boardId)}/uploads/${encodeURIComponent(uploadId)}/stage`,
      { save_template: saveTemplate, template_name: templateName }),
  discardUpload: (boardId: string, uploadId: string) =>
    request("DELETE", `${b(boardId)}/uploads/${encodeURIComponent(uploadId)}`),
  uploadTemplates: (boardId: string) => request<{ templates: UploadTemplate[] }>("GET", `${b(boardId)}/upload-templates`),
  deleteUploadTemplate: (boardId: string, id: string) =>
    request("DELETE", `${b(boardId)}/upload-templates/${encodeURIComponent(id)}`),

  // ── alerts ──
  alertMetrics: () => request<{ metrics: AlertMetric[]; interval_seconds: number }>("GET", "/api/alert-metrics"),
  parseRule: (boardId: string, text: string, scope: Scope) =>
    request<{ draft: RuleDraft }>("POST", `${b(boardId)}/alert-rules/parse`, { text, scope }),
  alertRules: (boardId: string) => request<{ rules: AlertRule[] }>("GET", `${b(boardId)}/alert-rules`),
  createRule: (boardId: string, spec: RuleSpec & { parse?: unknown }) =>
    request<{ rule: AlertRule }>("POST", `${b(boardId)}/alert-rules`, spec),
  updateRule: (boardId: string, ruleId: string, changes: Partial<RuleSpec & { enabled: boolean }>) =>
    request<{ rule: AlertRule }>("PATCH", `${b(boardId)}/alert-rules/${encodeURIComponent(ruleId)}`, changes),
  deleteRule: (boardId: string, ruleId: string) =>
    request("DELETE", `${b(boardId)}/alert-rules/${encodeURIComponent(ruleId)}`),
  checkRule: (boardId: string, ruleId: string) =>
    request<{ rule: AlertRule }>("POST", `${b(boardId)}/alert-rules/${encodeURIComponent(ruleId)}/check`),
  checkAlerts: (boardId: string) => request<{ rules: AlertRule[] }>("POST", `${b(boardId)}/alerts/check`),
  alerts: (boardId: string) => request<{ alerts: Alert[] }>("GET", `${b(boardId)}/alerts`),
  alertDetail: (boardId: string, alertId: string) =>
    request<AlertDetail>("GET", `${b(boardId)}/alerts/${encodeURIComponent(alertId)}`),
  setAlertStatus: (boardId: string, alertId: string, status: Exclude<AlertStatus, "converted" | "resolved">, snoozeHours = 24) =>
    request<{ alert: Alert }>("POST", `${b(boardId)}/alerts/${encodeURIComponent(alertId)}/status`,
      { status, snooze_hours: snoozeHours }),
  convertAlerts: (boardId: string, alertIds: string[], text: string, run: boolean) =>
    request<{ intent: IntentSummary; run: Run | null }>("POST", `${b(boardId)}/alerts/convert`,
      { alert_ids: alertIds, text, run }),

  users: () => request<{ users: User[] }>("GET", "/api/users"),
  createUser: (u: { username: string; password: string; display_name: string; role: string }) =>
    request<{ user: User }>("POST", "/api/users", u),
  updateUser: (username: string, changes: Partial<{ password: string; role: string; disabled: boolean }>) =>
    request<{ user: User }>("PATCH", `/api/users/${encodeURIComponent(username)}`, changes),
};

export function runEventsUrl(runId: string): string {
  return `/api/runs/${encodeURIComponent(runId)}/events`;
}
